import React, { useContext, useEffect, useState } from 'react';
import Tippy, { TippyProps, useSingleton } from '@tippyjs/react';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from 'src/reducers';
import { push } from 'connected-react-router';
import actions from 'src/actions';
import { UserTypes } from 'src/models/UserType';
import { UserRoles } from 'src/models/UserRole';
import Authorization from 'src/util/Authorization';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';

const AppNavBar: React.FC = () => {
    type page = 'home' | 'client' | 'personnel' | 'accounts-payable' | 'admin' | 'profile' | 'gis';

    const appContext = useSelector((state: IRootState) => state.appContext);
    const dispatch = useDispatch();
    const [menu, setMenu] = useState({ task: false, profile: false });
    const [currentSection, setCurrentSection] = useState<page>(undefined);
    const dialogContext = useContext<dialogCreators>(DialogContext);
    const [source, target] = useSingleton(undefined);
    // Code for the Report Error Button
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [emailed, setEmailed] = useState<boolean>(false);
    const [copied, setCopied] = useState<boolean>(false);

    const userHasAccess = appContext.currentUser.role === UserRoles.HR || appContext.currentUser.role === UserRoles.Admin || appContext.currentUser.role === UserRoles.ProspectManager;

    useEffect(() => {
        if (window.location.pathname.indexOf('/clients') >= 0) {
            setCurrentSection('client');
        } else if (window.location.pathname.indexOf('/personnel') >= 0) {
            setCurrentSection('personnel');
        } else if (window.location.pathname.indexOf('/accounts-payable') >= 0) {
            setCurrentSection('accounts-payable');
        } else if (window.location.pathname.indexOf('/admin') >= 0) {
            setCurrentSection('admin');
        } else if (window.location.pathname.indexOf('/profile') >= 0) {
            setCurrentSection('profile');
        } else if (window.location.pathname.indexOf('/gis') >= 0) {
            setCurrentSection('gis');
        // } else if (window.location.pathname.indexOf('/invoicing') >= 0) {
        //     setCurrentSection('invoicing');
        } else {
            setCurrentSection('home');
        }
    });

    useEffect(() => {
        if (!!showDialog) {
            setTimeout(() => {
                if (!emailed || !copied) {
                    setShowDialog(false);
                }
            }, 4000);
        }
    }, [showDialog]);

    const navigate = (path: string) => {
        setMenu({ task: false, profile: false });
        dispatch(push(path));
    };

    const taskPanel = (
        <div className="menu-drop-down-panel">
            <div style={{ padding: '1.5rem' }}>In Dev...</div>
        </div>
    );

    const profilePanel = (
        <div className="menu-drop-down-panel">
            <div className="profile-role-type">{UserTypes[appContext.currentUser.type]}</div>
            <div className="profile-user-info">
                <div className="text-center profile-user-info-name">{appContext.currentUser.username}</div>
                <div className="text-center profile-user-info-email">{appContext.currentUser.email}</div>
                <div className="text-center profile-user-info-role">Role: {UserRoles[appContext.currentUser.role]}</div>
            </div>
            {(Authorization.IsInRole(UserRoles.Admin, appContext.currentUser)) && (
                <div className="panel-section admin-link" onClick={() => navigate('/admin')}>
                    <i className="fas fa-cog" /> Administration
                </div>
            )}
            <div className="panel-section profile-link" onClick={() => navigate('/profile')}>
                <i className="fas fa-id-card" /> Profile
            </div>
            <div className="panel-section logout-link" onClick={() => dispatch(actions.auth.logout())}>
                <i className="fas fa-sign-out" /> Logout
            </div>
        </div>
    );

    const commonProps: Partial<TippyProps> = {
        placement: 'bottom-end',
        interactive: true,
        offset: [0, 4],
        delay: [0, 0],
        hideOnClick: true,
        trigger: 'click',
        onClickOutside: () => setMenu({ task: false, profile: false }),
    };

    const openSearch = () => {
        dialogContext.nav.searchDialog();
    };

    // const openSuperView = () => {
    //     dispatch(actions.layout.openSecondWindow());
    // };

    // const handleClick = () => {
    //    console.log('testing');
    // };

    const handleSendEmail = () => {
        setEmailed(true);
        setShowDialog(false);
    };

    const handleCopyEmail = async () => {
        let email = 'help@kudu.on.spiceworks.com';
        try {
            await navigator.clipboard.writeText(email);
            console.log('Email copied to clipboard');
            setCopied(true);
            setTimeout(() => {
                setShowDialog(false);
                setCopied(false);
            }, 1500);
        } catch (err) {
            console.log('Failed to copy: ', err);
        }
    };

    const handleMouseLeave = () => {
        setTimeout(() => {
            setShowDialog(false);
        }, 1000);
    };

    return (
        <div className="navbar-wrapper">
            <div className="app-navbar">
                <div className="centered">
                    <div className="left-aligned" onClick={() => dispatch(push('/'))}>
                        <span className="logo">{process.env.REACT_APP_TITLE}</span>
                    </div>
                    <div className="right-aligned">
                        <Tippy singleton={source} {...commonProps} />
                        <Tippy singleton={target} content={taskPanel} visible={menu.task} onClickOutside={() => setMenu({ ...menu, task: false })}>
                            <span className="task-menu" onClick={() => setMenu({ profile: false, task: !menu.task })}>
                                <i className="fas fa-tasks" /> Tasks
                            </span>
                        </Tippy>
                        <Tippy
                            singleton={target}
                            content={profilePanel}
                            visible={menu.profile}
                            onClickOutside={() => setMenu({ ...menu, profile: false })}
                        >
                            <span className="task-menu" onClick={() => setMenu({ task: false, profile: !menu.profile })}>
                                <i className="fas fa-user-circle" /> {appContext.currentUser.username}
                            </span>
                        </Tippy>
                    </div>
                </div>
            </div>
            <div className="app-second-navbar">
                <div className="centered">
                    <div className={`link ${currentSection === 'home' && 'selected'}`} onClick={() => navigate('/')}>
                        Home
                    </div>
                    <div className={`link ${currentSection === 'client' && 'selected'}`} onClick={() => navigate('/clients')}>
                        Clients
                    </div>
                    {userHasAccess && <div className={`link ${currentSection === 'personnel' && 'selected'}`} onClick={() => navigate('/personnel/home')}>
                        Personnel
                    </div>}
                    {userHasAccess && <div className={`link ${currentSection === 'accounts-payable' && 'selected'}`} onClick={() => navigate('/accounts-payable/vendors')}>
                        AP
                    </div>}
                    {userHasAccess && <div className={`link ${currentSection === 'gis' && 'selected'}`} onClick={() => navigate('/gis')}>
                        GIS
                    </div>}
                    {/* {userHasAccess && <div className={`link ${currentSection === 'invoicing' && 'selected'}`} onClick={() => navigate('/invoicing')}>
                        Invoicing
                    </div>} */}
                    <div className="link" onClick={openSearch}>
                        <i className="fas fa-search" /> Search
                    </div>
                    {/* <div className="link" onClick={openSuperView} style={{ borderRight: '1px solid #457aac' }}>
                        <i className="fas fa-external-link" /> SuperView
                    </div> */}
                    <div className="link" onClick={() => setShowDialog(true)} style={{ marginLeft: '37vw' }}>
                        <span style={{ margin: 'auto 3rem' }}>Report an Error</span>
                    </div>
                </div>
                {!!showDialog && (
                    <div
                        style={{
                            width: '300px',
                            position: 'absolute',
                            right: '8vw',
                            height: '50px',
                            display: 'flex',
                            gap: '2rem',
                            background: 'white',
                            alignItems: 'center',
                            border: '1px solid #457aac',
                            borderRadius: '5px',
                            justifyContent: 'space-around',
                        }}
                        onMouseLeave={handleMouseLeave}
                    >
                        <a onClick={handleSendEmail} href="mailto:help@kudu.on.spiceworks.com">
                            Send Email
                        </a>
                        <a onClick={handleCopyEmail}>{!!copied ? 'Copied!' : 'Copy Email'}</a>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AppNavBar;



// WEBPACK FOOTER //
// ./src/components/layout/AppNavBar.tsx